import { MIEJSCOWOSCI_URL, PRG_GEOMETRY_URL } from '../core/constants';
import { searchBtn, searchClose, searchInput, searchOverlay, searchResultsEl } from '../dom/elements';
import { esc } from '../utils/formatters';
import { iconMarkup } from '../utils/icons';
import { addOrUpdateGeoJsonSource, registerCursorHandlers, removeSourceAndLayers } from '../utils/maplibre-helpers';

var SEARCH_SOURCE_POINT = 'src-search';
var SEARCH_SOURCE_GEOMETRY = 'src-search-geom';
var SEARCH_LAYER_CIRCLE = 'lyr-search-circle';
var SEARCH_LAYER_FILL = 'lyr-search-fill';
var SEARCH_LAYER_OUTLINE = 'lyr-search-outline';
var SEARCH_LAYER_LINE = 'lyr-search-line';
var ALL_SEARCH_LAYERS = [SEARCH_LAYER_FILL, SEARCH_LAYER_OUTLINE, SEARCH_LAYER_LINE, SEARCH_LAYER_CIRCLE];

var ADDRESS_ZOOM = 18;
var LOCALITY_DEFAULT_ZOOM = 14;

var initialized = false;
var activePopup = null;
var activePopupCloseHandler = null;
var multiPickClickHandler = null;
var searchDebounceTimer = null;
var searchActiveIdx = -1;
var searchItems = [];
var geometryRequestSeq = 0;
var geometryAbortController = null;
var searchAbortController = null;
var SEARCH_TYPES = 'locality,street,place,address,bdot_area';

var onItemPickedCallback = null;
var onItemClearedCallback = null;

function searchTypeLabel(type) {
    if (type === 'locality') return 'Miejscowość';
    if (type === 'street') return 'Ulica';
    if (type === 'place') return 'Plac';
    if (type === 'address') return 'Adres';
    if (type === 'bdot_area') return 'Nazwany obszar';
    return '';
}

function geometryKindLabel(kind) {
    if (kind === 'point') return 'Punkt';
    if (kind === 'line') return 'Linia';
    if (kind === 'area') return 'Obszar';
    return '';
}

var SECTION_ORDER = ['address', 'street', 'locality', 'bdot_area', 'place'];

function sectionTitle(type) {
    if (type === 'address') return 'Adresy';
    if (type === 'street') return 'Ulice';
    if (type === 'locality') return 'Miejscowości';
    if (type === 'bdot_area') return 'Nazwane obszary';
    if (type === 'place') return 'Place';
    return '';
}

function sectionIcon(type) {
    if (type === 'address') return 'pin';
    if (type === 'street') return 'road';
    if (type === 'locality') return 'town';
    if (type === 'bdot_area') return 'town';
    if (type === 'place') return 'place';
    return 'loc';
}

function formatHitCount(total, query) {
    var word;
    if (total === 1) {
        word = 'trafienie';
    } else if (total % 10 >= 2 && total % 10 <= 4 && (total % 100 < 10 || total % 100 >= 20)) {
        word = 'trafienia';
    } else {
        word = 'trafień';
    }
    return total + ' ' + word + ' dla „' + query + '”';
}

function normalizeBbox(bbox) {
    if (!bbox) return null;
    if (typeof bbox === 'string') {
        bbox = bbox.split(',').map(function(value) { return parseFloat(value); });
    }
    if (!Array.isArray(bbox) || bbox.length !== 4) return null;
    var parsed = bbox.map(function(value) { return parseFloat(value); });
    for (var i = 0; i < parsed.length; i++) {
        if (!isFinite(parsed[i])) return null;
    }
    return parsed;
}

function clearSearchLayers(map) {
    if (activePopup) {
        var popup = activePopup;
        activePopup = null;
        if (activePopupCloseHandler) {
            try { popup.off('close', activePopupCloseHandler); } catch (e) { /* noop */ }
            activePopupCloseHandler = null;
        }
        try { popup.remove(); } catch (e) { /* popup may already be gone */ }
    }
    if (!map) return;

    if (multiPickClickHandler) {
        try { map.off('click', SEARCH_LAYER_CIRCLE, multiPickClickHandler); } catch (e) { /* noop */ }
        multiPickClickHandler = null;
    }

    removeSourceAndLayers(map, SEARCH_SOURCE_POINT, ALL_SEARCH_LAYERS);
    removeSourceAndLayers(map, SEARCH_SOURCE_GEOMETRY, ALL_SEARCH_LAYERS);
}

export function clearSearchSelection(map) {
    clearSearchLayers(map);
    if (typeof onItemClearedCallback === 'function') {
        onItemClearedCallback();
    }
}

function openSearch(map) {
    if (!searchOverlay) return;

    clearSearchLayers(map);
    searchOverlay.hidden = false;
    searchOverlay.style.display = '';
    searchInput.value = '';
    searchResultsEl.hidden = true;
    searchActiveIdx = -1;
    searchItems = [];
    setTimeout(function() { searchInput.focus(); }, 60);
}

function closeSearch() {
    if (!searchOverlay) return;

    searchOverlay.hidden = true;
    searchOverlay.style.display = 'none';
    searchInput.blur();
    searchResultsEl.hidden = true;
}

function buildItemMeta(item) {
    var typeLabel = searchTypeLabel(item.type);
    var kindLabel = geometryKindLabel(item.geometry_kind);
    var context = [item.miejscowosc, item.gmina, item.powiat, item.wojewodztwo].filter(Boolean);
    var location = context.filter(function(value, index) {
        return context.indexOf(value) === index;
    }).join(', ');
    var parts = [typeLabel, kindLabel];
    if (item.type === 'bdot_area' && item.rodzaj) parts.push(item.rodzaj);
    if (location) parts.push(location);
    return parts.filter(Boolean).join(' · ');
}

function renderSearchResults() {
    if (!searchItems.length) {
        var query = searchInput && searchInput.value ? searchInput.value.trim() : '';
        var emptyMsg = query
            ? 'Brak wyników dla „' + esc(query) + '”'
            : 'Brak wyników';
        searchResultsEl.innerHTML = '<div class="nm-search-empty">' + emptyMsg + '</div>';
        searchResultsEl.hidden = false;
        return;
    }

    var groups = {};
    for (var i = 0; i < searchItems.length; i++) {
        var item = searchItems[i];
        var key = item.type || 'other';
        if (!groups[key]) groups[key] = [];
        groups[key].push(i);
    }

    var orderedTypes = SECTION_ORDER.filter(function(t) { return groups[t]; });
    Object.keys(groups).forEach(function(t) {
        if (orderedTypes.indexOf(t) === -1) orderedTypes.push(t);
    });

    var query = searchInput && searchInput.value ? searchInput.value.trim() : '';
    var htmlString = '';
    if (query) {
        htmlString += '<div class="nm-search-summary">'
            + esc(formatHitCount(searchItems.length, query))
            + '</div>';
    }

    for (var g = 0; g < orderedTypes.length; g++) {
        var type = orderedTypes[g];
        var title = sectionTitle(type);
        var iconName = sectionIcon(type);
        htmlString += '<div class="nm-search-section" data-type="' + esc(type) + '">';
        if (title) {
            htmlString += '<div class="nm-search-section-title">' + esc(title) + '</div>';
        }
        var indices = groups[type];
        for (var j = 0; j < indices.length; j++) {
            var idx = indices[j];
            var entry = searchItems[idx];
            var classes = idx === searchActiveIdx ? ' is-active' : '';
            var meta = buildItemMeta(entry);
            htmlString += '<button class="nm-search-item' + classes + '" data-i="' + idx + '" type="button">'
                + '<span class="nm-search-item-icon nm-search-item-icon--' + esc(type) + '">'
                + iconMarkup(iconName, { size: 18 })
                + '</span>'
                + '<span class="nm-search-item-body">'
                + '<span class="nm-search-item-name">' + esc(entry.nazwa) + '</span>'
                + (meta ? '<span class="nm-search-item-meta">' + esc(meta) + '</span>' : '')
                + '</span>'
                + '</button>';
        }
        htmlString += '</div>';
    }

    searchResultsEl.innerHTML = htmlString;
    searchResultsEl.hidden = false;
}

function addPointPin(map, lngLat) {
    addOrUpdateGeoJsonSource(map, SEARCH_SOURCE_POINT, {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: lngLat }, properties: {} }]
    });
    map.addLayer({
        id: SEARCH_LAYER_CIRCLE,
        type: 'circle',
        source: SEARCH_SOURCE_POINT,
        paint: {
            'circle-radius': 10,
            'circle-color': '#f59e0b',
            'circle-stroke-color': '#b45309',
            'circle-stroke-width': 3,
            'circle-opacity': 0.85
        }
    });
}

function showPickedPopup(map, lngLat, target) {
    var popup = new maplibregl.Popup({ offset: 12, closeButton: true })
        .setLngLat(lngLat)
        .setHTML('<strong>' + esc(target.nazwa) + '</strong>')
        .addTo(map);
    activePopup = popup;
    activePopupCloseHandler = function() {
        if (activePopup !== popup) return;
        activePopup = null;
        activePopupCloseHandler = null;
        clearSearchLayers(map);
        if (typeof onItemClearedCallback === 'function') {
            onItemClearedCallback();
        }
    };
    popup.on('close', activePopupCloseHandler);
}

function fitToBbox(map, bbox, maxZoom) {
    try { map.stop(); } catch (e) { /* noop */ }
    map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], {
        padding: 70,
        maxZoom: maxZoom,
        duration: 600
    });
}

function flyToPoint(map, lngLat, zoom) {
    try { map.stop(); } catch (e) { /* noop */ }
    map.flyTo({ center: lngLat, zoom: zoom, duration: 600 });
}

function geojsonBoundsToBbox(geometry) {
    var west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;

    function visitCoord(coord) {
        if (!Array.isArray(coord) || coord.length < 2) return;
        if (typeof coord[0] === 'number') {
            if (coord[0] < west) west = coord[0];
            if (coord[0] > east) east = coord[0];
            if (coord[1] < south) south = coord[1];
            if (coord[1] > north) north = coord[1];
            return;
        }
        for (var i = 0; i < coord.length; i++) visitCoord(coord[i]);
    }

    if (geometry && geometry.coordinates) visitCoord(geometry.coordinates);
    if (!isFinite(west) || !isFinite(south) || !isFinite(east) || !isFinite(north)) return null;
    return [west, south, east, north];
}

function renderGeometryFeature(map, geometry, target, fallbackLngLat) {
    var geomType = geometry && geometry.type;
    var isLine = geomType === 'LineString' || geomType === 'MultiLineString';
    var isPolygon = geomType === 'Polygon' || geomType === 'MultiPolygon';

    if (!isLine && !isPolygon) return false;

    addOrUpdateGeoJsonSource(map, SEARCH_SOURCE_GEOMETRY, {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: geometry, properties: {} }]
    });

    if (isPolygon) {
        map.addLayer({
            id: SEARCH_LAYER_FILL,
            type: 'fill',
            source: SEARCH_SOURCE_GEOMETRY,
            paint: {
                'fill-color': '#f59e0b',
                'fill-opacity': 0.18
            }
        });
        map.addLayer({
            id: SEARCH_LAYER_OUTLINE,
            type: 'line',
            source: SEARCH_SOURCE_GEOMETRY,
            paint: {
                'line-color': '#b45309',
                'line-width': 2
            }
        });
    } else {
        map.addLayer({
            id: SEARCH_LAYER_LINE,
            type: 'line',
            source: SEARCH_SOURCE_GEOMETRY,
            paint: {
                'line-color': '#b45309',
                'line-width': 4,
                'line-opacity': 0.85
            }
        });
    }

    var bbox = geojsonBoundsToBbox(geometry);
    if (bbox) {
        fitToBbox(map, bbox, isLine ? 17 : 16);
    } else if (fallbackLngLat) {
        map.flyTo({ center: fallbackLngLat, zoom: LOCALITY_DEFAULT_ZOOM, duration: 600 });
    }

    return true;
}

function fetchAndRenderGeometry(map, target, lngLat) {
    if (!target || target.id === undefined || target.id === null) return;
    if (target.type !== 'street' && target.type !== 'place' && target.type !== 'bdot_area') return;

    var requestId = ++geometryRequestSeq;
    if (geometryAbortController) geometryAbortController.abort();
    geometryAbortController = new AbortController();
    var url = PRG_GEOMETRY_URL
        + '?type=' + encodeURIComponent(target.type)
        + '&id=' + encodeURIComponent(target.id);

    fetch(url, { signal: geometryAbortController.signal })
        .then(function(r) { return r.json(); })
        .then(function(data) {
            if (requestId !== geometryRequestSeq) return;
            if (!data || data.ok !== true || !data.geojson) return;
            if (!map.getSource(SEARCH_SOURCE_POINT) && !activePopup) return;

            renderGeometryFeature(map, data.geojson, target, lngLat);
        })
        .catch(function(err) {
            if (!err || err.name !== 'AbortError') {
                console.error('Pobieranie geometrii wybranej lokalizacji nie powiodło się:', err);
            }
        });
}

export function pickSearchItem(map, item, opts) {
    var target = item || (searchItems.length === 1 ? searchItems[0] : null);
    if (!target) return;
    var options = opts || {};
    var skipFly = !!options.skipFly;
    var silent = !!options.silent;

    closeSearch();
    clearSearchLayers(map);

    var lngLat = [target.lng, target.lat];
    var bbox = normalizeBbox(target.bbox);

    addPointPin(map, lngLat);
    showPickedPopup(map, lngLat, target);

    if (!silent && typeof onItemPickedCallback === 'function') {
        onItemPickedCallback(target);
    }

    if (target.type === 'street' || target.type === 'place' || target.type === 'bdot_area') {
        if (!skipFly) {
            if (bbox) {
                fitToBbox(map, bbox, target.type === 'street' ? 17 : 16);
            } else {
                flyToPoint(map, lngLat, LOCALITY_DEFAULT_ZOOM);
            }
        }
        fetchAndRenderGeometry(map, target, lngLat);
        return;
    }

    if (target.type === 'address') {
        if (!skipFly) {
            flyToPoint(map, lngLat, ADDRESS_ZOOM);
        }
        return;
    }

    if (skipFly) return;

    if (bbox) {
        fitToBbox(map, bbox, 16);
    } else {
        flyToPoint(map, lngLat, Math.max(map.getZoom(), LOCALITY_DEFAULT_ZOOM));
    }
}

function showSearchPins(map, items, onMapClickHandled) {
    clearSearchLayers(map);

    var features = [];
    var bounds = new maplibregl.LngLatBounds();
    for (var i = 0; i < items.length; i++) {
        var item = items[i];
        bounds.extend([item.lng, item.lat]);
        features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [item.lng, item.lat] },
            properties: { nazwa: item.nazwa, rodzaj: item.rodzaj || '' }
        });
    }

    addOrUpdateGeoJsonSource(map, SEARCH_SOURCE_POINT, { type: 'FeatureCollection', features: features });
    map.addLayer({
        id: SEARCH_LAYER_CIRCLE,
        type: 'circle',
        source: SEARCH_SOURCE_POINT,
        paint: {
            'circle-radius': 8,
            'circle-color': '#f59e0b',
            'circle-stroke-color': '#b45309',
            'circle-stroke-width': 2,
            'circle-opacity': 0.8
        }
    });
    registerCursorHandlers(map, SEARCH_LAYER_CIRCLE);

    var searchPopup = new maplibregl.Popup({ offset: 10, closeButton: true });
    multiPickClickHandler = function(e) {
        if (!e.features || !e.features.length) return;
        onMapClickHandled(e);
        var properties = e.features[0].properties;
        searchPopup.setLngLat(e.lngLat)
            .setHTML('<strong>' + esc(properties.nazwa) + '</strong>'
                + (properties.rodzaj ? '<br><span style="font-size:0.85em;color:#666">' + esc(properties.rodzaj) + '</span>' : ''))
            .addTo(map);
    };
    map.on('click', SEARCH_LAYER_CIRCLE, multiPickClickHandler);

    activePopup = searchPopup;
    if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 50, maxZoom: 14, duration: 600 });
    }
}

function performSearch(query) {
    if (searchAbortController) searchAbortController.abort();
    if (!query || query.length < 2) {
        searchResultsEl.hidden = true;
        searchItems = [];
        return;
    }

    searchAbortController = new AbortController();
    fetch(MIEJSCOWOSCI_URL + '?q=' + encodeURIComponent(query) + '&limit=20&types=' + encodeURIComponent(SEARCH_TYPES), {
        signal: searchAbortController.signal
    })
        .then(function(r) { return r.json(); })
        .then(function(data) {
            if (searchInput.value.trim() !== query) return;
            searchItems = (data.items || []).filter(function(item) {
                return item.lat && item.lng;
            });
            renderSearchResults();
        })
        .catch(function(err) {
            if (!err || err.name !== 'AbortError') console.error('Search failed:', err);
        });
}

export function initSearchModule(config) {
    if (initialized) return;
    initialized = true;

    var getMap = config.getMap;
    var onMapClickHandled = config.onMapClickHandled || function() {};
    onItemPickedCallback = typeof config.onItemPicked === 'function' ? config.onItemPicked : null;
    onItemClearedCallback = typeof config.onItemCleared === 'function' ? config.onItemCleared : null;

    if (searchBtn) {
        searchBtn.addEventListener('click', function() {
            openSearch(getMap());
        });
    }

    if (searchClose) {
        searchClose.addEventListener('click', closeSearch);
    }

    if (searchInput) {
        searchInput.addEventListener('input', function() {
            if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
            var query = searchInput.value.trim();
            searchActiveIdx = -1;
            searchDebounceTimer = setTimeout(function() {
                performSearch(query);
            }, 250);
        });

        searchInput.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
                closeSearch();
                return;
            }
            if (!searchItems.length) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                searchActiveIdx = Math.min(searchActiveIdx + 1, searchItems.length - 1);
                renderSearchResults();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                searchActiveIdx = Math.max(searchActiveIdx - 1, 0);
                renderSearchResults();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (searchActiveIdx >= 0 && searchItems[searchActiveIdx]) {
                    pickSearchItem(getMap(), searchItems[searchActiveIdx]);
                } else if (searchItems.length === 1) {
                    pickSearchItem(getMap(), searchItems[0]);
                } else if (searchItems.length > 1) {
                    var items = searchItems.slice();
                    closeSearch();
                    showSearchPins(getMap(), items, onMapClickHandled);
                }
            }
        });
    }

    if (searchResultsEl) {
        searchResultsEl.addEventListener('click', function(e) {
            var btn = e.target.closest('.nm-search-item');
            if (!btn) return;

            var index = parseInt(btn.getAttribute('data-i'), 10);
            if (searchItems[index]) {
                pickSearchItem(getMap(), searchItems[index]);
            }
        });
    }
}
